/**
 * 极简紫韵模版 - VIP会员页
 * 
 * 设计理念：纵向时间线式特权展示 + 横向3级价格梯度卡片
 * 与所有现有VIP页（对比表+套餐卡片/Hero+Tab）完全不同：
 * - Hero：紫色光晕 + 大标题 + 统计数字
 * - 特权区：纵向时间线布局（图标在左、说明在右，交错排列）
 * - 套餐区：水平3列价格卡片带渐变视觉层次
 * - 底部：FAQ折叠区
 */

import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import PurpleNavbar from "@/templates/purple/components/Navbar";
import PurpleFooter from "@/templates/purple/components/Footer";
import { useSite } from "@/context/SiteContext";
import { useAuth } from "@/hooks/useAuth";
import { getVipPlans } from "@/utils/adminApi";

// ==================== 支付配置 API ====================

interface PaymentConfig {
  alipay_enabled: boolean;
  wechat_enabled: boolean;
  qrcode_enabled: boolean;
  qrcode_priority: boolean;
  alipay_qrcode: string;
  wechat_qrcode: string;
}

async function fetchPaymentConfig(): Promise<PaymentConfig | null> {
  try {
    const token = localStorage.getItem("admin_token") || localStorage.getItem("token");
    const siteId = localStorage.getItem('current_site_id') || '';
    const res = await fetch("/api/admin/settings/payment", {
      method: "GET",
      headers: {
        "Authorization": token ? `Bearer ${token}` : "",
        'X-Site-Id': siteId,
      },
    });
    const data = await res.json();
    if (data.code === 0 && data.data) {
      return {
        alipay_enabled: !!data.data.alipay_enabled,
        wechat_enabled: !!data.data.wechat_enabled,
        qrcode_enabled: !!data.data.qrcode_enabled,
        qrcode_priority: !!data.data.qrcode_priority,
        alipay_qrcode: data.data.alipay_qrcode || "",
        wechat_qrcode: data.data.wechat_qrcode || "",
      };
    }
    return null;
  } catch (e) {
    console.error("读取支付配置失败", e);
    return null;
  }
}

export default function PurpleVipPage() {
  const { siteId, loading: siteLoading } = useSite();
  const { isLoggedIn } = useAuth();

  const [packages, setPackages] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState<number>(1);
  const [payMethod, setPayMethod] = useState<"wechat" | "alipay" | "qrcode">("wechat");
  const [showPayModal, setShowPayModal] = useState(false);
  const [paymentConfig, setPaymentConfig] = useState<PaymentConfig | null>(null);
  const [faqOpen, setFaqOpen] = useState<number | null>(null);

  useEffect(() => {
    if (siteLoading || !siteId) return;
    Promise.all([
      getVipPlans().then(setPackages).catch(() => {}),
      fetchPaymentConfig().then(setPaymentConfig).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [siteId, siteLoading]);

  const handleBuy = useCallback((pkg: any) => {
    if (!isLoggedIn) {
      window.location.href = `/login?redirect=/vip`;
      return;
    }
    setSelectedPlan(pkg.id);
    setShowPayModal(true);
  }, [isLoggedIn]);

  const showAlipay = !!paymentConfig?.alipay_qrcode && (!!paymentConfig?.qrcode_enabled || !!paymentConfig?.alipay_enabled);
  const showWechat = !!paymentConfig?.wechat_qrcode && (!!paymentConfig?.qrcode_enabled || !!paymentConfig?.wechat_enabled);

  const displayPackages = packages.length > 0 ? packages : [
    { id: 1, name: "月度VIP", price: "29.9", duration: 30, description: "适合短期需求", is_recommended: false },
    { id: 2, name: "季度VIP", price: "79.9", duration: 90, description: "性价比之选", is_recommended: true },
    { id: 3, name: "年度VIP", price: "239.9", duration: 365, description: "最高性价比", is_recommended: false },
  ];

  const currentPkg = displayPackages.find((p: any) => p.id === selectedPlan) || displayPackages[0];

  // 时间线特权
  const timelineFeatures = [
    {
      icon: "ri-file-list-3-line",
      color: "bg-purple-500",
      title: "无限发布",
      desc: "不再受发布数量限制，随心发布您的分类信息",
      benefits: ["突破月度限制", "批量管理", "自动草稿保存"],
    },
    {
      icon: "ri-pushpin-line",
      color: "bg-violet-500",
      title: "每日置顶",
      desc: "您的信息每日自动刷新置顶，获得优先展示机会",
      benefits: ["首页优先展示", "搜索排名第一", "置顶标识高亮"],
    },
    {
      icon: "ri-verified-badge-line",
      color: "bg-fuchsia-500",
      title: "加V认证",
      desc: "官方蓝V认证标识，提升信息可信度和点击率",
      benefits: ["官方认证标识", "专属身份徽章", "信任度提升"],
    },
    {
      icon: "ri-image-add-line",
      color: "bg-indigo-500",
      title: "高清大图",
      desc: "支持上传20张高清图片，全方位展示您的信息",
      benefits: ["20张图片上传", "无损原图", "图片水印保护"],
    },
    {
      icon: "ri-bar-chart-line",
      color: "bg-purple-600",
      title: "数据统计",
      desc: "详细的浏览数据和用户行为分析，助力优化信息",
      benefits: ["浏览量趋势", "用户画像", "转化率分析"],
    },
    {
      icon: "ri-customer-service-2-line",
      color: "bg-violet-600",
      title: "专属客服",
      desc: "VIP用户专享1对1客服，优先处理您的所有需求",
      benefits: ["1对1专属服务", "优先响应", "7×24小时"],
    },
  ];

  // FAQ
  const faqs = [
    { q: "VIP会员如何开通？", a: "选择适合您的套餐后，通过微信或支付宝扫码支付即可自动开通VIP权益。" },
    { q: "VIP时长如何计算？", a: "从支付成功时刻开始计算天数，到期后自动恢复为免费用户权限。" },
    { q: "可以升级套餐吗？", a: "可以，升级后剩余天数的价值将抵扣到新套餐中，非常划算。" },
    { q: "支持退款吗？", a: "开通后7天内如未使用VIP特权，可联系客服申请退款。" },
  ];

  return (
    <div className="min-h-screen bg-white">
      <PurpleNavbar />

      {/* ======== Hero区 ======== */}
      <section className="relative bg-gradient-to-br from-purple-700 via-violet-700 to-fuchsia-700 pt-20 pb-16 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-10 right-10 w-80 h-80 rounded-full bg-white/5 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-96 h-96 rounded-full bg-purple-300/10 blur-3xl" />
          {/* 装饰圆点 */}
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full bg-white/15"
              style={{
                width: `${8 + i * 4}px`,
                height: `${8 + i * 4}px`,
                top: `${20 + i * 12}%`,
                left: `${10 + i * 14}%`,
              }}
            />
          ))}
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/15 backdrop-blur-sm rounded-full text-white/90 text-xs font-semibold mb-6">
            <i className="ri-vip-crown-line text-purple-200" />
            VIP 会员专属特权
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white mb-4 leading-tight">
            升级<span className="text-purple-200">VIP</span>，解锁全部权益
          </h1>
          <p className="text-white/80 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            无限发布、每日置顶、加V认证，让您的信息触达更多用户
          </p>

          {/* 统计 */}
          <div className="grid grid-cols-3 gap-6 max-w-md mx-auto mt-10">
            {[
              { value: "10x", label: "曝光提升" },
              { value: "无限", label: "发布次数" },
              { value: "优先", label: "客户服务" },
            ].map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="text-2xl sm:text-3xl font-bold text-purple-200">{stat.value}</div>
                <div className="text-xs text-white/60 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======== 时间线特权展示 ======== */}
      <section className="py-16 bg-gray-50/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">VIP专享特权</h2>
            <p className="text-gray-400 text-sm">每一项特权，都为让您的信息更具价值</p>
          </div>

          {/* 时间线 */}
          <div className="relative">
            {/* 中线 */}
            <div className="absolute left-4 sm:left-1/2 top-0 bottom-0 w-0.5 bg-purple-100 -translate-x-1/2 hidden sm:block" />

            <div className="space-y-8 sm:space-y-12">
              {timelineFeatures.map((item, idx) => {
                const isLeft = idx % 2 === 0;
                return (
                  <div key={idx} className={`flex items-start gap-4 ${isLeft ? 'sm:flex-row' : 'sm:flex-row-reverse'}`}>
                    {/* 图标圆点 */}
                    <div className="relative z-10 shrink-0 sm:w-1/2 flex justify-center">
                      {/* 移动端：小图标在左 */}
                      <div className="sm:hidden">
                        <div className={`w-10 h-10 rounded-2xl ${item.color} flex items-center justify-center shadow-lg`}>
                          <i className={`${item.icon} text-white`} />
                        </div>
                      </div>
                      {/* 桌面端：居中的图标放在中线 */}
                      <div className="hidden sm:flex items-center justify-center w-full">
                        {isLeft ? (
                          <div className={`w-12 h-12 rounded-2xl ${item.color} flex items-center justify-center shadow-lg ml-auto mr-8`}>
                            <i className={`${item.icon} text-white text-lg`} />
                          </div>
                        ) : (
                          <div className={`w-12 h-12 rounded-2xl ${item.color} flex items-center justify-center shadow-lg mr-auto ml-8`}>
                            <i className={`${item.icon} text-white text-lg`} />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* 内容卡片 */}
                    <div className={`flex-1 sm:w-1/2 ${isLeft ? 'sm:pr-8' : 'sm:pl-8'}`}>
                      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-6 hover:shadow-md hover:border-purple-200 transition-all">
                        <h3 className="text-sm font-bold text-gray-800 mb-2">{item.title}</h3>
                        <p className="text-xs text-gray-500 mb-3 leading-relaxed">{item.desc}</p>
                        <div className="flex flex-wrap gap-2">
                          {item.benefits.map((b, bi) => (
                            <span key={bi} className="text-[10px] px-2.5 py-1 bg-purple-50 text-purple-600 rounded-full font-medium">
                              {b}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ======== 套餐卡片（水平3列） ======== */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">选择您的套餐</h2>
            <p className="text-gray-400 text-sm">找到最适合您的VIP方案</p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="animate-pulse bg-white rounded-3xl border border-gray-100 p-8">
                  <div className="w-1/2 h-6 bg-gray-200 rounded mx-auto mb-4" />
                  <div className="w-1/3 h-10 bg-gray-200 rounded mx-auto mb-6" />
                  <div className="space-y-3">
                    {[...Array(5)].map((_, j) => (
                      <div key={j} className="w-full h-4 bg-gray-100 rounded" />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {displayPackages.map((pkg, idx) => {
                const isRecommended = pkg.is_recommended;
                const gradients = [
                  'from-purple-50 to-violet-50 border-purple-200',
                  'from-purple-600 to-violet-700 border-purple-400',
                  'from-purple-50 to-violet-50 border-purple-200',
                ];
                return (
                  <div
                    key={pkg.id}
                    className={`relative bg-white rounded-3xl border-2 transition-all duration-300 flex flex-col overflow-hidden ${
                      isRecommended
                        ? 'border-purple-400 shadow-2xl shadow-purple-500/20 scale-105 z-10'
                        : 'border-gray-100 shadow-lg hover:border-purple-200 hover:shadow-xl'
                    }`}
                  >
                    {/* 推荐标签 */}
                    {isRecommended && (
                      <div className="bg-gradient-to-r from-purple-500 to-violet-500 text-white text-center py-2">
                        <span className="text-xs font-bold flex items-center justify-center gap-1">
                          <i className="ri-star-line text-[10px]" />最受欢迎
                        </span>
                      </div>
                    )}

                    <div className="p-8 flex-1 flex flex-col">
                      <div className="text-center mb-6">
                        <h3 className={`text-lg font-bold ${isRecommended ? 'text-purple-700' : 'text-gray-800'} mb-1`}>
                          {pkg.name}
                        </h3>
                        <p className="text-xs text-gray-400">{pkg.description}</p>
                      </div>

                      <div className="text-center mb-6">
                        <div className="flex items-baseline justify-center gap-1">
                          <span className="text-sm text-gray-400">¥</span>
                          <span className={`text-4xl font-extrabold ${isRecommended ? 'text-purple-600' : 'text-gray-800'}`}>
                            {pkg.price}
                          </span>
                        </div>
                        <p className="text-xs text-gray-400 mt-1">
                          {pkg.duration >= 365
                            ? `仅 ¥${(Number(pkg.price) / 12).toFixed(1)}/月`
                            : `共 ${pkg.duration} 天`}
                        </p>
                      </div>

                      <ul className="space-y-3 mb-8 flex-1">
                        {[
                          "无限发布信息",
                          "每日置顶推荐",
                          "加V官方认证",
                          "20张高清图片",
                          "完整联系方式",
                          "数据统计分析",
                        ].map((feature, fi) => (
                          <li key={fi} className="flex items-start gap-2.5">
                            <i className={`ri-check-line text-sm mt-0.5 ${isRecommended ? 'text-purple-500' : 'text-green-500'}`} />
                            <span className="text-sm text-gray-600">{feature}</span>
                          </li>
                        ))}
                      </ul>

                      <button
                        onClick={() => handleBuy(pkg)}
                        className={`w-full py-3.5 rounded-2xl font-bold text-sm transition-all ${
                          isRecommended
                            ? 'bg-gradient-to-r from-purple-600 to-violet-600 text-white shadow-lg shadow-purple-500/25 hover:from-purple-700 hover:to-violet-700'
                            : 'border-2 border-purple-200 text-purple-600 hover:bg-purple-50'
                        }`}
                      >
                        立即开通
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-8 text-center">
            <p className="text-xs text-gray-400">
              开通即表示同意{" "}
              <Link to="/page/register-agreement" className="text-purple-500 hover:text-purple-600">服务协议</Link>
              {" "}和{" "}
              <Link to="/page/privacy" className="text-purple-500 hover:text-purple-600">隐私政策</Link>
            </p>
          </div>
        </div>
      </section>

      {/* ======== FAQ折叠区 ======== */}
      <section className="py-16 bg-gray-50/50">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8">
            <h2 className="text-xl font-bold text-gray-800 mb-1">常见问题</h2>
            <p className="text-sm text-gray-400">关于VIP会员的常见疑问</p>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, idx) => (
              <div
                key={idx}
                className="bg-white rounded-2xl border border-gray-100 overflow-hidden"
              >
                <button
                  onClick={() => setFaqOpen(faqOpen === idx ? null : idx)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-purple-50/30 transition-colors"
                >
                  <span className="text-sm font-semibold text-gray-800">{faq.q}</span>
                  <i className={`ri-arrow-${faqOpen === idx ? 'up' : 'down'}-s-line text-gray-400 transition-transform`} />
                </button>
                {faqOpen === idx && (
                  <div className="px-5 pb-5 text-sm text-gray-500 leading-relaxed">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <PurpleFooter />

      {/* ======== 支付弹窗 ======== */}
      {showPayModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-gray-800 text-lg">扫码支付</h3>
              <button
                onClick={() => setShowPayModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
              >
                <i className="ri-close-line text-gray-500" />
              </button>
            </div>

            {/* 支付切换 */}
            <div className="flex gap-2 mb-4">
              {showWechat && (
                <button
                  onClick={() => setPayMethod("wechat")}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-medium transition-all ${
                    payMethod === "wechat"
                      ? "bg-green-50 text-green-700 border-2 border-green-400"
                      : "bg-gray-50 text-gray-500 border-2 border-transparent hover:border-gray-200"
                  }`}
                >
                  <i className="ri-wechat-pay-fill" />微信
                </button>
              )}
              {showAlipay && (
                <button
                  onClick={() => setPayMethod("alipay")}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-medium transition-all ${
                    payMethod === "alipay"
                      ? "bg-blue-50 text-blue-700 border-2 border-blue-400"
                      : "bg-gray-50 text-gray-500 border-2 border-transparent hover:border-gray-200"
                  }`}
                >
                  <i className="ri-alipay-fill" />支付宝
                </button>
              )}
            </div>

            <div className="text-center">
              {payMethod === "alipay" && showAlipay && (
                <div>
                  <p className="text-xs text-gray-500 mb-3">请使用支付宝扫码付款</p>
                  <img src={paymentConfig?.alipay_qrcode} alt="支付宝收款码" className="w-52 h-52 mx-auto rounded-xl border border-gray-200" />
                </div>
              )}
              {payMethod === "wechat" && showWechat && (
                <div>
                  <p className="text-xs text-gray-500 mb-3">请使用微信扫码付款</p>
                  <img src={paymentConfig?.wechat_qrcode} alt="微信收款码" className="w-52 h-52 mx-auto rounded-xl border border-gray-200" />
                </div>
              )}
              {!showAlipay && !showWechat && (
                <>
                  <div className="w-44 h-44 mx-auto mb-4 bg-gray-100 rounded-xl flex items-center justify-center">
                    <i className="ri-qr-code-line text-5xl text-gray-300" />
                  </div>
                  <p className="text-sm text-gray-500 mb-1">支付功能暂未启用</p>
                  <p className="text-xs text-gray-400">请联系管理员配置收款码</p>
                </>
              )}

              <p className="text-2xl font-bold text-purple-600 mt-4 mb-1">¥{currentPkg?.price || "29.9"}</p>
              <p className="text-xs text-gray-400">{currentPkg?.name || "VIP套餐"} · {currentPkg?.duration || 30}天</p>
            </div>

            <div className="mt-5 p-3.5 bg-purple-50 rounded-xl text-xs text-purple-700 text-center font-medium">
              <i className="ri-information-line mr-1" />支付成功后VIP权益将自动开通
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
